/* eslint no-empty: ["error", { "allowEmptyCatch": true }] */

/**
 * OneBlock Widgets.
 * @module oneblock-widgets.mjs
 * @version 1.0.03
 * @summary 23-10-2019
 * @author Mads Stoumann
 * @description Custom Widgets and extensions for OneBlock, can be added though data-js, data-js-event and data-js-param.
 * NOTE: Either import or paste the code for custom widgets in this module.
 */

import { oneBlockBuildLineItemTrackingParameters } from './oneblock-tracking.mjs';
import { track_add_to_cart } from 'shared/services/tracking/basketTrackingService.ts';

/**
 * @function countDown
 * @param {Event} event
 * @param {Node} hook
 * @param {String} selector
 * @param {String} timeStamp
 * @param {String} lblDays
 * @param {String} lblHours
 * @param {String} lblMins
 * @param {String} lblSecs
 * @description Renders a countDown-widget in `selector`-element. Creadits to: https://codepen.io/SitePoint/pen/QpwRxz
 */

export function countDown(
	event,
	hook,
	selector,
	timeStamp,
	lblDays,
	lblHours,
	lblMins,
	lblSecs
) {
	const ob = hook.closest('.c-ob');
	if (!ob) {
		return false;
	}
	const clock = ob.querySelector(`.${selector}`);
	const endTime = new Date(timeStamp).getTime();

	function getRemainingTime(deadline) {
		const currentTime = new Date().getTime();
		return deadline - currentTime;
	}

	function pad(value) {
		return ('0' + Math.floor(value)).slice(-2);
	}

	function showTime() {
		const remainingTime = getRemainingTime(endTime);
		const seconds = pad((remainingTime / 1000) % 60);
		const minutes = pad((remainingTime / (60 * 1000)) % 60);
		const hours = pad((remainingTime / (60 * 60 * 1000)) % 24);
		const days = pad(remainingTime / (24 * 60 * 60 * 1000));
		clock.innerHTML = `
		<div class="c-clock">
			<div class="c-clock__item">${days}<span>${lblDays}</span></div>
			<div class="c-clock__item">${hours}<span>${lblHours}</span></div>
			<div class="c-clock__item">${minutes}<span>${lblMins}</span></div>
			<div class="c-clock__item">${seconds}<span>${lblSecs}</span></div>
		</div>`;

		if (remainingTime >= 1000) {
			requestAnimationFrame(showTime);
		}
	}
	requestAnimationFrame(showTime);
}

/**
 * @function scrollToElm
 * @param {Event} event
 * @param {Node} hook
 * @param {String} selector
 * @description Scrolls to element matching `selector`, or to the bottom of hook-parent
 */
export function scrollToElm(event, hook, selector) {
	event.preventDefault();
	const ob = hook.closest('.c-ob');
	if (!ob) {
		return false;
	}
	const element = selector ? ob.querySelector(`.${selector}`) : ob;
	const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
	if (element) {
		const rect = element.getBoundingClientRect();
		if (isIE11) {
			window.scrollTo(0, selector ? rect.top : rect.bottom);
		} else {
			window.scrollBy({
				top: selector ? rect.top : rect.bottom,
				behavior: 'smooth',
			});
		}
	}
}

/**
 * @function sampleCatalogContent
 * @param {Event} event
 * @param {Node} sourceElm
 * @param {String} idStr
 */
export async function sampleCatalogContent(event, sourceElm, idStr) {
	event.preventDefault();
	const ids = idStr.split(';');
	try {
		const ob = sourceElm.closest('[data-catalog-content-vm]');
		const lineItemSource = !!ob
			? ob.dataset.lineItemSource || 'OneblockProductSampler'
			: 'OneblockProductSampler';
		const apiUrl = sourceElm.dataset.jsApiurl;
		const result = await (
			await fetch(apiUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json;charset=utf-8',
				},
				body: JSON.stringify({
					productCodes: ids,
					lineItemSource: lineItemSource,
					trackingParameters:
						oneBlockBuildLineItemTrackingParameters(lineItemSource),
				}),
			})
		).json();

		//ga4 tracking - uses the new IHasBasketChangesTrackingParameters Interface
		track_add_to_cart(result);
		result.quickSample = true;

		let event = new CustomEvent('samplesadded', { detail: result });
		window.dispatchEvent(event);
		return result;
	} catch (ex) {
		console.error('An error occured while adding samples to basket', ex);
		return null;
	}
}

/**
 * @function activateConsumerSignup
 * @param {Event} event
 * @param {Node} sourceElm
 * @param {String} idStr
 */
export function activateConsumerSignup(event) {
	event.preventDefault();
	let activateSignUpEvent = new CustomEvent('activateconsumersignup');
	document.dispatchEvent(activateSignUpEvent);
}
